import {
  PANE_MARGIN_TOP_PX,
  PANE_MARGIN_BOTTOM_PX,
  PANE_MARGIN_MAX_SHARE,
  FLAT_RANGE_PADDING_RATIO,
} from "../constants";

/**
 * Vertical scale: price ↔ y in CSS pixels of the pane;
 * y = price * scale + offset (scale is negative: prices grow upwards).
 * Always autoscaled, as on moon.com: every frame the visible bars fit between
 * the margins (in pixels: room for the toolbar and the high / low labels).
 * There is no manual vertical zoom or pan; zooming time changes the visible
 * bars and so the fit.
 */
export default class PriceScale {
  /**
   * @param {{ marginTop: number, marginBottom: number }} [margins] - CSS px; the main
   *   pane by default, an indicator pane passes its own
   */
  constructor({ marginTop = PANE_MARGIN_TOP_PX, marginBottom = PANE_MARGIN_BOTTOM_PX } = {}) {
    this.marginTopPx = marginTop;
    this.marginBottomPx = marginBottom;
    this.height = 0;
    this.minPrice = 0;
    this.maxPrice = 1;
  }

  setHeight(height) {
    this.height = height;
  }

  /**
   * @param {{ min: number, max: number } | null} range - prices of the visible
   *   bars; ignored when it is null
   */
  autoscale(range) {
    if (!range) return;

    let { min, max } = range;

    if (max <= min) {
      const padding = Math.abs(max) * FLAT_RANGE_PADDING_RATIO || 1;

      min -= padding;
      max += padding;
    }

    this.minPrice = min;
    this.maxPrice = max;
  }

  /** @returns {{ min: number, max: number }} prices at the autoscale margins */
  get range() {
    return { min: this.minPrice, max: this.maxPrice };
  }

  /** CSS px above the highest price of the range. */
  get marginTop() {
    return Math.min(this.marginTopPx, this.height * PANE_MARGIN_MAX_SHARE);
  }

  /** CSS px below the lowest price of the range. */
  get marginBottom() {
    return Math.min(this.marginBottomPx, this.height * PANE_MARGIN_MAX_SHARE);
  }

  get scale() {
    const innerHeight = this.height - this.marginTop - this.marginBottom;

    return -innerHeight / (this.maxPrice - this.minPrice);
  }

  get offset() {
    return this.marginTop - this.maxPrice * this.scale;
  }

  /** How much the price changes per CSS pixel. */
  get pricePerPixel() {
    return 1 / -this.scale;
  }

  /**
   * Transform for prices stored relative to `priceBase` (series buffers):
   * y = p * scale + offset.
   *
   * @param {number} priceBase
   * @returns {{ scale: number, offset: number }} CSS px
   */
  relativeTransform(priceBase) {
    const { scale, offset } = this;

    return { scale, offset: offset + priceBase * scale };
  }

  priceToY(price) {
    return price * this.scale + this.offset;
  }

  yToPrice(y) {
    return (y - this.offset) / this.scale;
  }
}
